import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  PANEL_CONFIG_CHANGED,
  SHOW_DISCARD_DIALOG,
  TEST_ID,
  MODEL_ID,
  TABLE_ASSET_ID,
  DRAWER_TYPE,
  INCIDENT_ID,
  TEST_OVERVIEW_SIDEPANEL_TAB,
  TEST_OVERVIEW_TAB_EXECUTION_INFO,
  PARENT_TEST_ID,
} from '../constants/urlParams';
import useSearchParams from './useSearchParams';
import useSelectedEnv from './useSelectedEnv';

const useSidePanelParams = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlEnvId = useSelectedEnv();
  const [envId, setEnvId] = useState(urlEnvId);

  const isConfigChanged = searchParams.get(PANEL_CONFIG_CHANGED);
  const showDiscardDialog = searchParams.get(SHOW_DISCARD_DIALOG);
  const selectedTestId = searchParams.get(TEST_ID);
  const selectedModelId = searchParams.get(MODEL_ID);
  const tableAssetId = searchParams.get(TABLE_ASSET_ID);
  const incidentId = searchParams.get(INCIDENT_ID);
  const drawerType = searchParams.get(DRAWER_TYPE);

  const selectedDrawerType =
    drawerType ||
    (!incidentId && (selectedTestId || selectedModelId) ? 'test' : '');

  const openSidePanel = (
    drawerType: 'test' | 'asset' | 'cloud_test',
    id: string,
    srcType?: 'model' | 'test',
    tab?: string,
  ) => {
    if (isConfigChanged) {
      searchParams.set(SHOW_DISCARD_DIALOG, 'true');
    } else {
      searchParams.delete(DRAWER_TYPE);
      searchParams.delete(MODEL_ID);
      searchParams.delete(TEST_ID);
      searchParams.delete(TABLE_ASSET_ID);
      searchParams.delete(PANEL_CONFIG_CHANGED);
      searchParams.delete(SHOW_DISCARD_DIALOG);
      searchParams.delete(PARENT_TEST_ID);

      searchParams.set(DRAWER_TYPE, drawerType);
      if (drawerType === 'test' || drawerType === 'cloud_test') {
        const paramName = srcType === 'model' ? MODEL_ID : TEST_ID;
        searchParams.set(paramName, id);
        searchParams.set(
          TEST_OVERVIEW_SIDEPANEL_TAB,
          TEST_OVERVIEW_TAB_EXECUTION_INFO,
        );
      } else if (drawerType === 'asset') {
        searchParams.set(TABLE_ASSET_ID, id);
      }

      if (tab) {
        searchParams.set(TEST_OVERVIEW_SIDEPANEL_TAB, tab);
      }
    }

    setSearchParams(searchParams);
  };

  const deleteParams = useCallback(() => {
    searchParams.delete(DRAWER_TYPE);
    searchParams.delete(MODEL_ID);
    searchParams.delete(TEST_ID);
    searchParams.delete(TEST_OVERVIEW_SIDEPANEL_TAB);
    searchParams.delete(TABLE_ASSET_ID);
    searchParams.delete(PANEL_CONFIG_CHANGED);
    searchParams.delete(SHOW_DISCARD_DIALOG);
    searchParams.delete(PARENT_TEST_ID);
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  const closeSidePanel = useCallback(() => {
    if (isConfigChanged) {
      searchParams.set(SHOW_DISCARD_DIALOG, 'true');
      setSearchParams(searchParams);
    } else {
      deleteParams();
    }
  }, [deleteParams, isConfigChanged, searchParams, setSearchParams]);

  const isOpen = useMemo(
    () =>
      location.pathname.includes('/test-triage') ? false : !!selectedDrawerType,
    [location.pathname, selectedDrawerType],
  );

  const handleDiscardChanges = useCallback(() => {
    deleteParams();
    setSearchParams(searchParams);
  }, [deleteParams, searchParams, setSearchParams]);

  const handleCloseDiscardDialog = useCallback(() => {
    searchParams.delete(SHOW_DISCARD_DIALOG);
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  const handlePanelConfigChanged = useCallback(
    (isChanged: boolean) => {
      if (isChanged) {
        searchParams.set(PANEL_CONFIG_CHANGED, 'true');
      } else {
        searchParams.delete(PANEL_CONFIG_CHANGED);
      }
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  const handlePanelConfigSaved = useCallback(() => {
    searchParams.delete(PANEL_CONFIG_CHANGED);
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (urlEnvId !== envId) {
      // setSearchParams navigates relative to the pre-switch pathname, so an
      // unconditional close would override redirects made on env change.
      if (selectedDrawerType) {
        closeSidePanel();
      }
      setEnvId(urlEnvId);
    }
  }, [envId, urlEnvId, selectedDrawerType, closeSidePanel]);

  useEffect(() => {
    if (!isOpen) {
      searchParams.delete(SHOW_DISCARD_DIALOG);
      setSearchParams(searchParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return {
    isOpen,
    openSidePanel,
    closeSidePanel,
    selectedDrawerType,
    sourceType: selectedTestId ? 'test' : 'model',
    selectedId: selectedTestId || selectedModelId || tableAssetId,
    onDiscardChanges: handleDiscardChanges,
    onCloseDiscardDialog: handleCloseDiscardDialog,
    onPanelConfigChanged: handlePanelConfigChanged,
    onPanelConfigSaved: handlePanelConfigSaved,
    showDiscardDialog,
  };
};

export default useSidePanelParams;
